var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Api_ShareLink_Feed_Type, } from './types/api_sharelink';
import { setupRedaxiosClient } from './setupRedaxiosClient';
export const needsChannelParam = (params) => {
    if (!params.feed_type) {
        return false;
    }
    const requireds = [
        Api_ShareLink_Feed_Type.CHANNEL_TIMELINE,
        Api_ShareLink_Feed_Type.CHANNEL_CONTEXTUAL,
        Api_ShareLink_Feed_Type.CHANNEL_PERSONALIZED,
        Api_ShareLink_Feed_Type.PLAYLIST,
    ];
    return requireds.includes(params.feed_type);
};
export const validateApiShareLinkParams = (params) => {
    const messageBase = `Invalid Sharelink API parameters.`;
    if (needsChannelParam(params) && !params.channel) {
        throw new Error(`${messageBase} feed_type is "${params.feed_type}" but channel is not defined`);
    }
    if (params.feed_type === Api_ShareLink_Feed_Type.HASHTAG_POPULAR &&
        !params.hashtag) {
        throw new Error(`${messageBase} feed_type is "${Api_ShareLink_Feed_Type.HASHTAG_POPULAR}" but hashtag is not defined`);
    }
    if (params.feed_type === Api_ShareLink_Feed_Type.PLAYLIST &&
        !params.playlist_id) {
        throw new Error(`${messageBase} feed_type is ${Api_ShareLink_Feed_Type.PLAYLIST} but playlist_id is not defined`);
    }
    return true;
};
export const createApiService = (apiHost, token, context, __setupFunc = setupRedaxiosClient) => {
    const api = __setupFunc(apiHost, token, context);
    const getToken = () => token;
    const getUser = (username) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/api/users/${encodeURIComponent(username)}`);
        return response.data;
    });
    const getCurrentUser = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!token) {
            return null;
        }
        const response = yield api.get(`/api/me`);
        return response.data;
    });
    const getUserVideos = (username) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/api/users/${encodeURIComponent(username)}/videos`);
        return response.data;
    });
    const getVideo = (videoId) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/api/videos/${videoId}`);
        return response.data;
    });
    const getLiveStreamMessages = (liveStreamId, since = 1) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/api/live_streams/${liveStreamId}/messages?since=${since}`);
        return (yield response.data);
    });
    const getLiveStreamEvents = (liveStreamId, since = 1, abortSignal) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/api/live_streams/${liveStreamId}/actions?since=${since}`, {
            signal: abortSignal,
        });
        return (yield response.data);
    });
    const getLiveStreamStateSnapshot = (liveStreamId, since = 1) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/api/live_streams/${liveStreamId}/state_snapshot?since=${since}`);
        return (yield response.data);
    });
    const getShareLink = (params) => __awaiter(void 0, void 0, void 0, function* () {
        if (validateApiShareLinkParams(params)) {
            const response = yield api.post(`/api/player_urls`, params);
            return response.data;
        }
        return undefined;
    });
    const getHashtag = (tag) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/api/hashtags/${tag}`);
        return response.data;
    });
    const createEmbedUserTimelineFeed = (username, pageSize, firstVideoId) => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL(`${api.defaults.baseURL}/embed/v2/channels/${username}/timeline_feeds`);
        pageSize && url.searchParams.append('page_size', `${pageSize}`);
        firstVideoId && url.searchParams.append('first_video_id', firstVideoId);
        const response = yield api.post(url.href);
        return response.data;
    });
    const createEmbedUserTimelineHashtagFeed = (username, hashtagExpression, pageSize, firstVideoId) => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL(`${api.defaults.baseURL}/embed/v2/channel_hashtags/${username}/feeds`);
        pageSize && url.searchParams.append('page_size', `${pageSize}`);
        const response = yield api.post(url.href, {
            hashtag_filter_expression: hashtagExpression ? hashtagExpression : '',
            first_video_id: firstVideoId ? firstVideoId : '',
        });
        return response.data;
    });
    const createEmbedPlaylistFeed = (playlistId, pageSize, firstVideoId) => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL(`${api.defaults.baseURL}/embed/v2/playlists/${playlistId}/feeds`);
        pageSize && url.searchParams.append('page_size', `${pageSize}`);
        firstVideoId && url.searchParams.append('first_video_id', firstVideoId);
        const response = yield api.post(url.href);
        return response.data;
    });
    const createEmbedPlaylistCollectionFeed = (id, pageSize, firstPlaylistId) => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL(`${api.defaults.baseURL}/embed/v2/playlist_groups/${id}/`);
        pageSize && url.searchParams.append('page_size', `${pageSize}`);
        firstPlaylistId &&
            url.searchParams.append('first_playlist_id', firstPlaylistId);
        const response = yield api.get(url.href);
        return response.data;
    });
    const getPlaylistCollection = (id) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/embed/v2/playlist_groups/${id}`);
        return response.data;
    });
    const createEmbedHashtagFeed = (hashtag, pageSize, firstVideoId) => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL(`${api.defaults.baseURL}/embed/v2/hashtags/${hashtag}/feeds`);
        pageSize && url.searchParams.append('page_size', `${pageSize}`);
        firstVideoId && url.searchParams.append('first_video_id', firstVideoId);
        const response = yield api.post(url.href);
        return response.data;
    });
    const createEmbedPublisherClientFeed = (publisherClientId, guestId, pageUrl, pageSize, firstVideoId) => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL(`${api.defaults.baseURL}/embed/v2/publisher_clients/${publisherClientId}/feeds`);
        url.searchParams.append('guest_id', guestId);
        url.searchParams.append('page_url', pageUrl);
        pageSize && url.searchParams.append('page_size', `${pageSize}`);
        firstVideoId && url.searchParams.append('first_video_id', firstVideoId);
        const response = yield api.post(url.href);
        return response.data;
    });
    const getMoreFeedItems = (feed, pageSize) => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL(`${api.defaults.baseURL}${feed.next_page}`);
        pageSize && url.searchParams.append('page_size', `${pageSize}`);
        const response = yield api.get(url.href);
        return response.data;
    });
    const getPublisherClient = (key, value) => __awaiter(void 0, void 0, void 0, function* () {
        const url = new URL(`${api.defaults.baseURL}/embed/v2/publisher_client`);
        url.searchParams.append(key, value);
        const response = yield api.get(url.href);
        return response.data;
    });
    const getChannel = (username) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/embed/v2/channels/${encodeURIComponent(username)}`);
        return response.data;
    });
    const getChannelById = (encodedId) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.get(`/embed/v2/channels/id/${encodeURIComponent(encodedId)}`);
        return response.data;
    });
    const getPlaylistFromCohorts = (channel, cohorts) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.post(`/embed/v2/playlists/from_cohort_params`, Object.assign({ channel }, cohorts));
        return response.data;
    });
    const createVideoInteractionResult = (interaction, guest_id, answer, email) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api.post(`/api/interactions/${interaction.id}/submit_response`, {
            guest_id,
            value: answer,
            email: email,
        });
        return response.data;
    });
    const getPaymentIntent = (businessStoreId, cartItems, orderToken, linkPersistentToken) => __awaiter(void 0, void 0, void 0, function* () {
        const payload = {
            cart_items: cartItems,
        };
        if (orderToken) {
            payload.order_token = orderToken;
        }
        if (linkPersistentToken) {
            payload.link_persistent_token = linkPersistentToken;
        }
        const response = yield api
            .post(`/api/commerce/${businessStoreId}/payment_intent`, payload)
            .catch((error) => {
            return error;
        });
        return response.status >= 200 && response.status < 300
            ? response === null || response === void 0 ? void 0 : response.data
            : null;
    });
    const updatePaymentIntent = (orderToken, shippingAdress) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api
            .post(`/api/commerce/finalize_payment_intent`, {
            order_token: orderToken,
            shipping_address: shippingAdress,
        })
            .catch((error) => {
            return error;
        });
        return response.status >= 200 && response.status < 300
            ? response === null || response === void 0 ? void 0 : response.data
            : null;
    });
    const placeOrder = (videoId, delay, email, orderToken, guestId, sessionId, embedInstanceId) => __awaiter(void 0, void 0, void 0, function* () {
        const url = `/api/commerce/confirm_order?video_id=${videoId}`;
        const response = yield api
            .post(url, {
            delay,
            email,
            order_token: orderToken,
            guest_id: guestId,
            session_id: sessionId,
            embed_instance_id: embedInstanceId,
        })
            .catch((error) => {
            return error;
        });
        return response.status >= 200 && response.status < 300
            ? response === null || response === void 0 ? void 0 : response.data
            : null;
    });
    const getOrder = (orderToken) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api
            .get(`/api/commerce/retrieve_order?order_token=${orderToken}`)
            .catch((error) => {
            return error;
        });
        return response.status >= 200 && response.status < 300
            ? response === null || response === void 0 ? void 0 : response.data
            : null;
    });
    const deleteOrder = (orderToken) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api
            .delete(`/api/commerce/cancel_order?order_token=${orderToken}`)
            .catch((error) => {
            return error;
        });
        return response.status >= 200 && response.status < 300 ? true : false;
    });
    // TODO: @zeitgeistf should encode this PII at the top of the data pipeline so we can remove this endpoint
    // This is being used in @embed/components/Player3/components/Checkout/CheckoutContent/CheckoutContent.tsx
    // and @embed/components/Player3/components/Checkout/OrderContent/OrderContent.tsx
    const encodeEmail = (email) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield api
            .get(`/api/commerce/encode_email?email=${email}`)
            .catch((error) => {
            return error;
        });
        return response.status >= 200 && response.status < 300
            ? response === null || response === void 0 ? void 0 : response.data
            : {
                email,
                encoded_email: '',
            };
    });
    return {
        getToken,
        getCurrentUser,
        getChannel,
        getChannelById,
        getLiveStreamMessages,
        getLiveStreamEvents,
        getPlaylistFromCohorts,
        getLiveStreamStateSnapshot,
        getUser,
        getUserVideos,
        getVideo,
        getHashtag,
        getShareLink,
        createEmbedUserTimelineFeed,
        createEmbedUserTimelineHashtagFeed,
        createEmbedPlaylistFeed,
        createEmbedHashtagFeed,
        createEmbedPublisherClientFeed,
        getMoreFeedItems,
        getPublisherClient,
        createVideoInteractionResult,
        getPlaylistCollection,
        createEmbedPlaylistCollectionFeed,
        getPaymentIntent,
        placeOrder,
        updatePaymentIntent,
        getOrder,
        deleteOrder,
        encodeEmail,
    };
};
